import { getProvider, type LogContext } from './context.js';
import { NON_ERROR_CAUSE_PLACEHOLDER } from './constants.js';
import { asError, emittableErrorMessage, isErrorLike } from './error-fields.js';
import { maskNationalIds, redactLogValue } from './redact.js';
import { isLogLevelEnabled } from './level.js';
import { emitToSinks, type LogEntry, type LogLevel } from './sinks.js';

export type { LogContext } from './context.js';
export type { LogEntry, LogLevel, LogSink } from './sinks.js';
export { registerLogSink } from './sinks.js';
/** @internal */
export { clearLogSinks } from './sinks.js';
export { isLogLevel, setMinLogLevel } from './level.js';
/** @internal */
export { isSensitiveLogKey, maskNationalIds } from './redact.js';
export { asError, emittableErrorMessage } from './error-fields.js';
export {
  scrubTelemetryAttributesInPlace,
  scrubTelemetryRecord,
  scrubTelemetryString,
  scrubTelemetryValue,
} from './telemetry-scrub.js';

export function runWithContext<T>(context: LogContext, fn: () => T): T {
  const p = getProvider();
  const parentContext = p.getStore();
  const mergedContext = parentContext ? { ...parentContext, ...context } : context;
  return p.run(mergedContext, fn);
}

/** Reads a string `code` property (e.g. `ENOTFOUND`, `ECONNRESET`) off any value. */
function readErrorCode(value: unknown): string | undefined {
  if (value !== null && typeof value === 'object' && 'code' in value) {
    const code = value.code;
    if (typeof code === 'string') {
      return code;
    }
  }
  return undefined;
}

/** Renders a `cause` as a short string, by the same rule as every sink. */
function stringifyCause(cause: unknown): string {
  return isErrorLike(cause)
    ? emittableErrorMessage(cause.name, cause.message)
    : NON_ERROR_CAUSE_PLACEHOLDER;
}

/** Flat context fields describing why an operation failed. */
export type ErrorCauseContext = { cause?: string; code?: string };

/**
 * Extracts the underlying failure reason from an error's `cause` as flat context
 * fields to spread *alongside* the preserved error instance — never in place of
 * it:
 *
 *     logger.error('Request failed', { error, ...errorCauseContext(error) });
 *
 * For `fetch` failures `error.message` is always the generic `"fetch failed"`;
 * the real reason (DNS `ENOTFOUND` vs connection `ECONNRESET`) lives on
 * `error.cause`, and its `code` is dropped when an `Error` cause is serialized.
 * Surfacing `cause`/`code` as discrete keys makes failure modes greppable
 * in logs while `error` keeps its stack for
 * `captureException` grouping. Returns `{}` when there is nothing to add.
 */
export function errorCauseContext(error: unknown): ErrorCauseContext {
  const context: ErrorCauseContext = {};
  const cause: unknown =
    error !== null && typeof error === 'object' && 'cause' in error ? error.cause : undefined;
  if (cause !== undefined && cause !== null) {
    context.cause = stringifyCause(cause);
  }
  const code = readErrorCode(cause) ?? readErrorCode(error);
  if (code !== undefined) {
    context.code = code;
  }
  return context;
}

/**
 * Log context for a failed upstream NHN response. NHN error bodies routinely
 * echo the queried fnr, so the raw body must never reach a log sink; only the
 * status, the `x-correlation-id` header (for tracing the request with NHN
 * support) and the static RFC 7807 fields `type`/`title` are allowlisted.
 * `detail` and `instance` repeat request parameters — never add them.
 */
export type UpstreamErrorLogContext = {
  status: number;
  correlationId?: string;
  problemType?: string;
  problemTitle?: string;
};

export function upstreamErrorLogContext(
  response: Response,
  problem?: { type?: string | null; title?: string | null },
): UpstreamErrorLogContext {
  return {
    status: response.status,
    correlationId: response.headers.get('x-correlation-id') ?? undefined,
    problemType: problem?.type ?? undefined,
    problemTitle: problem?.title ?? undefined,
  };
}

export const createLogger = (scope: string, boundContext?: LogContext) => {
  const log = (level: LogLevel, message: string, context?: LogContext): LogEntry | undefined => {
    if (!isLogLevelEnabled(level)) {
      return undefined;
    }
    const asyncContext = getProvider().getStore();
    const mergedContext = { ...asyncContext, ...boundContext, ...context };
    if (mergedContext.error !== undefined && !isErrorLike(mergedContext.error)) {
      mergedContext.error = asError(mergedContext.error);
    }
    const entry: LogEntry = { level, scope, message, context: mergedContext };
    // Console output is redacted (errors serialized, direct identifiers masked);
    // sinks below get the live entry so `error` stays an `Error` instance.
    console.log(
      `[${scope}]`,
      JSON.stringify({
        level,
        message: maskNationalIds(message),
        context: redactLogValue(mergedContext),
      }),
    );
    emitToSinks(entry);
    return entry;
  };

  return {
    debug: (message: string, context?: LogContext) => log('debug', message, context),
    info: (message: string, context?: LogContext) => log('info', message, context),
    warn: (message: string, context?: LogContext) => log('warn', message, context),
    error: (message: string, context?: LogContext) => log('error', message, context),
    child: (childContext: LogContext) => createLogger(scope, { ...boundContext, ...childContext }),
  };
};

export type Logger = ReturnType<typeof createLogger>;
